import React, { useState, useEffect, useCallback } from "react";
import {
  Box,
  Flex,
  Text,
  Button,
  Card,
  CardBody,
  Image,
  Heading,
  Skeleton,
  Center,
  Badge,
} from "@chakra-ui/react";
import { FaShoppingBasket } from "react-icons/fa";
import { Auth } from "aws-amplify";
import axios from "axios";
import AWS from "aws-sdk";
import { useNavigate, useLocation } from "react-router-dom";
import { ServiceDrawer } from "../Drawer/ServiceDrawer";
import { apiAuth, apiGetAuth } from "../../../Backend/api";

// Configure AWS for S3 image fetching
AWS.config.update({
  region: "us-west-2",
  credentials: new AWS.Credentials(
    process.env.REACT_APP_PUBLIC_KEY,
    process.env.REACT_APP_SECRET_KEY
  ),
});

export const ServicesSection = ({ room, booking, onDrawerStateChange }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const [services, setServices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [imageUrls, setImageUrls] = useState({});
  const [selectedService, setSelectedService] = useState(null);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [priceMap, setPriceMap] = useState({});
  const [priceLoading, setPriceLoading] = useState(true);
  

  const handleOpenServices = useCallback(() => {
    navigate('/services', { state: { room, booking } });
  }, [navigate, room, booking]);

  const handleServiceClick = useCallback((service) => {
    setSelectedService(service);
    setDrawerOpen(true);
    onDrawerStateChange?.(true);
  }, [onDrawerStateChange]);

  const handleDrawerClose = useCallback(() => {
    setDrawerOpen(false);
    setSelectedService(null);
    onDrawerStateChange?.(false);
  }, [onDrawerStateChange]);

  // Memoize the fetchImageUrls function to prevent infinite loops
  const fetchImageUrls = useCallback(async (servicesData) => {
    const newImageUrls = {};
    const cloudfrontBaseUrl = "https://dl6ri8ttf37m3.cloudfront.net"; // Image handler CloudFront URL

    for (const service of servicesData) {
        const imageKey = service.images?.[0];
        if (!imageKey) continue;

        // If it's already a full URL, use it directly
        if (imageKey.startsWith("http")) {
          newImageUrls[service.id] = imageKey;
          continue;
        }

        // Construct optimized CloudFront URL
        const optimizedUrl = `${cloudfrontBaseUrl}/public/${encodeURIComponent(imageKey)}?width=600&format=webp`;

        newImageUrls[service.id] = optimizedUrl;
    }

    setImageUrls(newImageUrls);
  }, []);

  const fetchServices = async () => {
    if (!room?.id) {
      setLoading(false);
      return;
    }

    try {
      // apiGetAuth follows REACT_APP_PAY_API_URL (localhost in dev) — the
      // URL was hardcoded to prod before, which broke local testing.
      const data = await apiGetAuth(`/upsells/by-room/${room.id}?limit=10`);

      if (data.success) {
        const activeServices = data.data.upsells.filter(service => service.isActive);
        // Take exactly 2 services (minimum and maximum)
        const limitedServices = activeServices.slice(0, 2);
        setServices(limitedServices);
        
        // Fetch S3 URLs for all images
        await fetchImageUrls(limitedServices);
      }
    } catch (err) {
      console.error('Error fetching services:', err);
      setError('Failed to load services');
    } finally {
      setLoading(false);
    }
  };

  const formatPrice = (service) => {
    const preview = priceMap[service.id];
    // Hour-tier upsells price from their cheapest duration — "From $X".
    const hasTiers = Array.isArray(service.timeOptions) && service.timeOptions.length > 0;
    const prefix = hasTiers ? "From " : "";
    if (priceLoading) return "Loading...";
    if (preview) {
      const cur = preview.currency.toUpperCase();
      return `${prefix}${(preview.total / 100).toLocaleString("en-CA", { style: "currency", currency: cur })} ${cur}`;
    }
    if (!service.price) return "Free";
    return `${prefix}$${service.price}`;
  };

  const getNights = () => {
    if (!booking?.checkInDate || !booking?.checkOutDate) return undefined;
    const n = Math.ceil(
      (new Date(booking.checkOutDate) - new Date(booking.checkInDate)) / (1000 * 60 * 60 * 24)
    );
    return n > 0 ? n : undefined;
  };

  const fetchPricePreviews = async (serviceList) => {
    const ids = serviceList.filter((s) => s.price > 0).map((s) => s.id);
    if (ids.length === 0) { setPriceLoading(false); return; }
    try {
      const body = { upsellIds: ids };
      const nights = getNights();
      if (nights) body.nights = nights;
      const res = await apiAuth("/v2/upsells/payment/price-preview", body);
      if (res.success && res.data?.items) {
        const map = {};
        res.data.items.forEach((item) => { map[item.upsellId] = item; });
        setPriceMap(map);
      }
    } catch (err) {
      console.error("ServicesSection - Price preview failed:", err);
    } finally {
      setPriceLoading(false);
    }
  };

  useEffect(() => {
    fetchServices();
  }, [fetchImageUrls, room?.id]);

  useEffect(() => {
    if (services.length > 0) {
      fetchPricePreviews(services);
    }
  }, [services]);

  // Section header: small mobile label, real heading + "View all" link on desktop
  const SectionHeader = ({ showLink }) => (
    <Flex justify="space-between" align="center" pb={4}>
      <Text fontSize={{ base: "12px", md: "18px" }} fontWeight={800} color={"white"}>
        Services
      </Text>
      {showLink && (
        <Button
          variant="link"
          color="#817EFF"
          fontSize="14px"
          fontWeight={700}
          display={{ base: "none", md: "inline-flex" }}
          onClick={handleOpenServices}
        >
          View all services →
        </Button>
      )}
    </Flex>
  );

  if (loading) {
    return (
      <Box px={{ base: 4, md: 0 }} pt={4} pb={4}>
        <SectionHeader showLink={false} />
        <Flex direction="row" gap={{ base: 4, md: 5 }} mb={4}>
          {[1, 2].map((i) => (
            <Card key={i} flex="1" bg="rgba(255, 255, 255, 0.05)" borderRadius="12px">
              <Skeleton h={{ base: "95px", md: "160px" }} />
              <CardBody p={3} pt={4}>
                <Skeleton height="16px" mb={2} />
                <Skeleton height="12px" />
              </CardBody>
            </Card>
          ))}
        </Flex>
        <Button
          leftIcon={<FaShoppingBasket />}
          bg={"#817EFF"}
          color="white"
          h="42px"
          w="full"
          display={{ base: "flex", md: "none" }}
          variant="solid"
          borderRadius="10px"
          fontSize="14px"
          _active={{ bg: "#615edc" }}
          _hover={{ bg: "#615edc" }}
        >
          Show More Services
        </Button>
      </Box>
    );
  }

  if (error) {
    return (
      <Box px={{ base: 4, md: 0 }} pt={4} pb={4}>
        <SectionHeader showLink={true} />
        <Center h="100px">
          <Text fontSize="12px" color="red.400">
            {error}
          </Text>
        </Center>
        <Button
          onClick={handleOpenServices}
          leftIcon={<FaShoppingBasket />}
          bg={"#817EFF"}
          color="white"
          h="42px"
          w="full"
          display={{ base: "flex", md: "none" }}
          variant="solid"
          borderRadius="10px"
          fontSize="14px"
          _active={{ bg: "#615edc" }}
          _hover={{ bg: "#615edc" }}
        >
          Show More Services
        </Button>
      </Box>
    );
  }

  if (services.length === 0) {
    return null;
  }

  return (
    <Box px={{ base: 4, md: 0 }} pt={4} pb={4}>
      <SectionHeader showLink={true} />

      <Flex direction="row" gap={{ base: 4, md: 5 }}>
        {services.map((service) => {
          const hasImage = !!imageUrls[service.id];
          return (
            <Card
              key={service.id}
              direction={"column"}
              overflow="hidden"
              variant="outline"
              bg="rgba(255, 255, 255, 0.05)"
              border="1px solid"
              borderColor="rgba(255, 255, 255, 0.1)"
              borderRadius="12px"
              cursor="pointer"
              transition="all 0.3s ease"
              flex="1"
              minH="180px"
              display="flex"
              mb={4}
              onClick={() => handleServiceClick(service)}
              _hover={{
                transform: 'translateY(-2px)',
                boxShadow: '0 4px 15px rgba(0, 0, 0, 0.2)',
                borderColor: 'rgba(129, 126, 255, 0.3)',
              }}
            >
              {hasImage ? (
                <>
                  <Image
                    objectFit="cover"
                    h={{ base: "95px", md: "160px" }}
                    w="full"
                    src={imageUrls[service.id]}
                    alt={service.name}
                  />
                  <CardBody p={3} pt={4}>
                    <Heading size="md" color="white" fontSize="16px" fontWeight="700" mb={2} textAlign="left">
                      {service.name}
                    </Heading>
                    <Flex justify="space-between" align="center">
                      {priceLoading ? (
                        <Skeleton height="14px" width="60px" />
                      ) : (
                        <Text fontSize="14px" fontWeight="700" color="#817EFF">
                          {formatPrice(service)}
                        </Text>
                      )}
                    </Flex>
                  </CardBody>
                </>
              ) : (
                <Flex direction="column" align="flex-start" justify="center" flex="1" minH="180px" px={3} py={4}>
                  <Heading size="md" color="white" fontSize="16px" fontWeight="700" mb={2} noOfLines={1} textAlign="left">
                    {service.name.length > 20 ? `${service.name.substring(0, 20)}...` : service.name}
                  </Heading>
                  {service.description && (
                    <Text fontSize="12px" color="#A0AEC0" mb={2} noOfLines={3} textAlign="left">
                      {service.description}
                    </Text>
                  )}
                  {service.price > 0 && (
                    priceLoading ? (
                      <Skeleton height="14px" width="80px" />
                    ) : (
                      <Text fontSize="14px" color="#817EFF" fontWeight={800} textAlign="left">
                        {formatPrice(service)}
                      </Text>
                    )
                  )}
                </Flex>
              )}
            </Card>
          );
        })}
      </Flex>

      <Button
        onClick={handleOpenServices}
        leftIcon={<FaShoppingBasket />}
        bg={"#817EFF"}
        color="white"
        h="42px"
        w="full"
        display={{ base: "flex", md: "none" }}
        variant="solid"
        borderRadius="10px"
        fontSize="14px"
        _active={{ bg: "#615edc" }}
        _hover={{ bg: "#615edc" }}
      >
        Show More Services
      </Button>

      <ServiceDrawer
        isOpen={drawerOpen}
        onClose={handleDrawerClose}
        service={selectedService}
        booking={booking}
      />
    </Box>
  );
}; 